import { realNumber } from 'utils/math_util';

import productsModule from 'domains/products';

import type { ProductTemplateAPIData } from 'types/catalog_types';

export function calculateBoundDimension(
  bound: string,
  nodeType: 'image' | 'mask',
  product: ProductTemplateAPIData,
  customSize: { height?: number; width?: number }
) {
  let w;
  let h;

  // The bounds of a node always correspond to the natural/unrotated dimensions of a product
  const { height, width } = productsModule.utils.calculateProductSize(product, customSize, true);

  switch (nodeType) {
    case 'image':
      // Remove frame size from image nodes
      // Custom frame/wrap will be 0 on non-custom products
      const { frame, wrap } = productsModule.utils.getBleedFrameWrapOptions(product.custom_options);

      w = width - frame;
      h = height - frame;

      // Remove the wrap size from image nodes on bordered canvas products
      if (product.custom_options?.isBordered) {
        w -= wrap;
        h -= wrap;
      }
      break;
    case 'mask':
      w = width;
      h = height;
      break;
    default:
      throw new Error('getCalculatedBound needs a nodeType');
  }

  return calculateBoundsExpression(bound, w, h);
}

export function calculateBoundsExpression(
  bound: string,
  productWidth: number,
  productHeight: number
) {
  let newBound = parseFloat(bound);

  const stringMath = {
    '+': function (x: number, y: number) {
      return x + y;
    },
    '-': function (x: number, y: number) {
      return x - y;
    },
  };

  if (!realNumber(newBound)) {
    const expressionRegx = /(\[\w+\])/;
    const expression = expressionRegx.exec(bound);
    let size;
    let boundExpression;

    if (expression) {
      switch (expression[0]) {
        case '[width]':
          boundExpression = bound.split('[width]');
          size = productWidth;
          break;
        case '[height]':
          boundExpression = bound.split('[height]');
          size = productHeight;
          break;
        case '[width_center]':
          boundExpression = bound.split('[width_center]');
          size = productWidth / 2;
          break;
        case '[height_center]':
          boundExpression = bound.split('[height_center]');
          size = productHeight / 2;
          break;
        default:
          // If bound wasn't a number or a defined bound expression, return 0 for it
          return 0;
      }

      // get math operator
      const operator = boundExpression[1].slice(0, 1);
      if (operator) {
        // get size offset
        const sizeOffset = parseFloat(boundExpression[1].split(operator)[1]);

        if (!realNumber(sizeOffset)) return 0;

        // apply math
        newBound = stringMath[operator as '+' | '-'](size, sizeOffset);
      } else {
        newBound = size;
      }
    } else {
      return 0;
    }
  }

  return newBound;
}
